export interface ProjectionLot {
  expiresOnIso: string
  units: number
}

export const addDaysIso = (iso: string, days: number): string => {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10)
}

/** Jours ISO de `fromIso` à `toIso` inclus, vide si `toIso` précède `fromIso`. */
export const daysBetweenIso = (fromIso: string, toIso: string): string[] => {
  const days: string[] = []
  for (let day = fromIso; day <= toIso; day = addDaysIso(day, 1)) days.push(day)
  return days
}

/** Jours de `fromIso` à `toIso` (dates ISO, heure ignorée), négatif si `toIso` précède. */
export const dayDiffIso = (fromIso: string, toIso: string): number =>
  Math.round(
    (Date.parse(`${toIso.slice(0, 10)}T00:00:00Z`) -
      Date.parse(`${fromIso.slice(0, 10)}T00:00:00Z`)) /
      86_400_000,
  )

export const totalLotUnits = (lots: readonly ProjectionLot[]): number =>
  lots.reduce((sum, lot) => sum + lot.units, 0)

export const addLot = (lots: readonly ProjectionLot[], lot: ProjectionLot): ProjectionLot[] =>
  lot.units <= 0
    ? [...lots]
    : [...lots, lot].sort((a, b) => a.expiresOnIso.localeCompare(b.expiresOnIso))

export const sellFifo = (
  lots: readonly ProjectionLot[],
  units: number,
  dayIso: string,
): { lots: ProjectionLot[]; sold: number } => {
  const wanted = Math.max(0, units)
  let remaining = wanted
  const next: ProjectionLot[] = []
  for (const lot of lots) {
    if (remaining > 0 && lot.expiresOnIso > dayIso) {
      const taken = Math.min(lot.units, remaining)
      remaining -= taken
      if (lot.units - taken > 0) next.push({ ...lot, units: lot.units - taken })
    } else {
      next.push(lot)
    }
  }
  return { lots: next, sold: wanted - remaining }
}

export const expireLots = (
  lots: readonly ProjectionLot[],
  dayIso: string,
): { lots: ProjectionLot[]; expired: number } => {
  const limit = addDaysIso(dayIso, 1)
  const kept = lots.filter((lot) => lot.expiresOnIso > limit)
  return { lots: kept, expired: totalLotUnits(lots) - totalLotUnits(kept) }
}

export const shortDlcUnits = (lots: readonly ProjectionLot[], dayIso: string): number => {
  const limit = addDaysIso(dayIso, 1)
  return totalLotUnits(lots.filter((lot) => lot.expiresOnIso <= limit))
}
